/**
 * The scene model: what to draw, and nothing about how.
 *
 * Plain data only — resolved positions, sizes, paths, labels and identifiers.
 * No drawing calls, no React, no DOM (D13, requirement 11.2). That is what lets
 * the SVG renderer, a future canvas renderer and the export path all consume one
 * description, and what makes their outputs comparable.
 *
 * Coordinates are scene units (SVG user units at zoom 1). Zoom and pan are a
 * renderer transform over the scene, never a change to it (requirement 12.8).
 */
import type { Run } from "../design/noteText";
import type { StepPreview } from "../scripts/preview";
import type { LiquidUnits } from "../workspace/units";
import type {
  DesignKind,
  LabelSide,
  Direction,
  EdgeType,
  GroupDefinitionDocument,
  NodeRole,
  PortKind,
  Severity,
  TemplateDocument,
} from "../design/document";

export interface Point {
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface SceneFinding {
  code: string;
  message: string;
  severity: Severity;
  path: string | null;
}

/** A finding-bearing element. `worstSeverity` is null when there are none. */
export interface Badged {
  findings: readonly SceneFinding[];
  worstSeverity: Severity | null;
}

export interface ScenePort extends Badged {
  /** The hierarchical port reference edges use, e.g. "valve_1.port_a". */
  ref: string;
  name: string;
  kind: PortKind;
  isExternal: boolean;
  /** Closed on purpose; drawn as an x in place of the port. */
  deadEnd: boolean;
  position: Point;
  /** Which way an edge leaves the port, already rotated with the node. */
  direction: Direction;
}

/**
 * Where a node's position came from. `placement` is Phase 2's physical layout;
 * `schematic` is editor coordinates; `unplaced` means neither exists and the
 * node sits at the origin until the editor gives it a position.
 */
export type PositionSource = "placement" | "schematic" | "unplaced";

export interface SceneNode extends Badged {
  ref: string;
  role: NodeRole;
  label: string;
  glyph: string;
  badge: string;
  /** Centre of the symbol box. */
  centre: Point;
  /** Unrotated symbol size; the renderer rotates about `centre`. */
  size: Size;
  rotation: number; // radian
  /** Mirrored left-right in the symbol's frame, before rotation. */
  mirrored: boolean;
  positionSource: PositionSource;
  templateVersionId: string | null;
  /** True when the pinned template could not be resolved; drawn as a fallback. */
  templateMissing: boolean;
  /** The block instance this node is a member of, if any. */
  blockInstance: string | null;
  /**
   * An expanded block whose frame this node sits inside without being one of
   * its members. Drawn faded, so what a block holds is what it looks like it
   * holds. Presentation only.
   */
  strayIn?: string;
  /** The line this part is mounted on (D40), when it is: it sits on it, not between two lines. */
  mountedOn?: string;
  /** Which side of the symbol the label is drawn, in screen terms. */
  labelSide: LabelSide;
  ports: readonly ScenePort[];
}

export interface SceneEdgeEnd {
  ref: string;
  point: Point;
  /** Null when the endpoint is concealed inside a collapsed block. */
  direction: Direction | null;
  /**
   * The endpoint's port is inside a collapsed block and is not one of its
   * exposed ports, so the edge is drawn to the block's centre. The definition
   * should expose it; the scene does not hide the connection.
   */
  concealed: boolean;
}

export interface SceneEdge extends Badged {
  ref: string;
  type: EdgeType;
  from: SceneEdgeEnd;
  to: SceneEdgeEnd;
  /**
   * The drawn polyline. Never lengthened to be proportional to hydraulic length
   * (D18): its length means nothing, which is why the true length is a label.
   */
  path: readonly Point[];
  /** True hydraulic length in metre, from parameters (invariant 4). */
  hydraulicLength: number | null;
  /** `hydraulicLength` formatted for display, e.g. "20 mm". */
  lengthLabel: string | null;
  labelPosition: Point;
  /** True when the designer placed the bends; false when routed automatically (D23). */
  manual: boolean;
  /** Draw with the serpentine glyph: a long channel that is folded when built. */
  serpentine: boolean;
  /** Draw with the mixing glyph. */
  mixing: boolean;
  /** An air line (D30): drawn dotted, so gas and liquid read apart. */
  air: boolean;
  blockInstance: string | null;
}

export interface SceneBlock extends Badged {
  ref: string;
  name: string;
  definitionVersionId: string;
  /** False when the definition version could not be resolved. */
  definitionResolved: boolean;
  collapsed: boolean;
  glyph: string;
  /**
   * Collapsed: the symbol's centre and size. Expanded: a frame around the
   * members, for drawing an outline and a title.
   */
  centre: Point;
  size: Size;
  /** The collapsed symbol's orientation. An expanded frame is never turned. */
  rotation: number; // radian
  mirrored: boolean;
  /** Collapsed only: the exposed ports. Expanded blocks show members' ports. */
  ports: readonly ScenePort[];
  memberRefs: readonly string[];
}

export interface SceneGrid {
  spacing: number;
  visible: boolean;
}

/**
 * What is drawn: annotations, and which kinds of line. View state, like the
 * grid: it changes what is shown, never the design. Carried on the scene so
 * every renderer, and an export, draws the same thing the screen does
 * (invariant 7).
 */
export interface SceneAnnotations {
  /** Component labels under their symbols. */
  labels: boolean;
  /** Channel lengths along their lines. */
  dimensions: boolean;
  /** Liquid-carrying lines: straight and mixing channels. */
  fluidLines: boolean;
  /** Gas-carrying lines (D30). */
  airLines: boolean;
  /** Notes written on the schematic. */
  notes: boolean;
  /**
   * A wash over one side of the external interfaces: inside them (the
   * consumable) or outside them. None unless asked for, and never kept
   * between openings: a design opens without it.
   */
  wash: Wash;
  /**
   * With a wash: show only the washed side, and hide everything else, so one
   * side can be looked at (or exported) on its own. Off without a wash, and,
   * like the wash, never kept between openings.
   */
  isolate?: boolean;
}

export type Wash = "none" | "inside" | "outside";

/** Everything drawn, and no wash. */
export const ALL_ANNOTATIONS: SceneAnnotations = {
  labels: true,
  dimensions: true,
  fluidLines: true,
  airLines: true,
  notes: true,
  wash: "none",
};

/** Whether a line of this type is drawn under `annotations`. */
export function lineShown(annotations: SceneAnnotations, type: EdgeType | undefined): boolean {
  return type === "air_line" ? annotations.airLines : annotations.fluidLines;
}

/**
 * A note as drawn: its text broken into lines, and the box they need. Sized
 * from the text alone, so the same note is the same size on every renderer.
 */
/** One drawn line of a note: its runs of marked text, indented if it is in a list. */
export interface SceneNoteLine {
  /** Scene units from the text's left edge: a list item's indent. */
  indent: number;
  /** A bullet or number, on the first line of a list item. */
  marker: string | null;
  runs: readonly Run[];
}

export interface SceneNote {
  ref: string;
  /** The stored text, markup and all. */
  text: string;
  lines: readonly SceneNoteLine[];
  /** Top-left corner. */
  position: Point;
  size: Size;
  /** The component it moves with, if that component is drawn. */
  anchor: string | null;
}

/**
 * The tube through a clamp-on part the view has hidden (showing only the
 * single-use set): the straight run between the part's two tube ports, which
 * its symbol draws anyway. The tube is the set's; the part on it is not.
 */
export interface SceneTubeRun {
  /** The part it runs through, for reference only: it is not drawn or hit. */
  ref: string;
  from: Point;
  to: Point;
  air: boolean;
}

export interface Scene {
  /** A script step's preview, when one is shown (D42). */
  preview?: StepPreview | null;
  /** What the preview's volumes and flows read in (D49); a tubing set's when unset. */
  units?: LiquidUnits;
  /** Tubes through clamp-on parts hidden by showing only the set. */
  tubeRuns: readonly SceneTubeRun[];
  /** Notes, when shown: none at all when the view hides them. */
  notes: readonly SceneNote[];
  /** Visible nodes: excludes members of collapsed blocks. */
  nodes: readonly SceneNode[];
  /** Visible edges: excludes edges internal to a collapsed block. */
  edges: readonly SceneEdge[];
  blocks: readonly SceneBlock[];
  grid: SceneGrid;
  annotations: SceneAnnotations;
  /** Refs of what the wash covers, on the side it picks; empty with no wash. */
  washed: ReadonlySet<string>;
  bounds: Bounds;
  /** Edges whose endpoints do not resolve, and so cannot be drawn. */
  unresolvedEdges: readonly string[];
  /** Findings about the design as a whole, or about elements not drawn. */
  unattachedFindings: readonly SceneFinding[];
}

// --- Inputs -----------------------------------------------------------------

export interface DefinitionEntry {
  name: string;
  content: GroupDefinitionDocument;
}

/** Resolved library content, keyed by version id. */
export interface SceneLibrary {
  templates: Readonly<Record<string, TemplateDocument>>;
  definitions: Readonly<Record<string, DefinitionEntry>>;
}

/** View state: what the user chose to see. None of it touches the document. */
export interface SceneView {
  /** Block instances drawn collapsed (requirement 8.6). */
  collapsed: ReadonlySet<string>;
  grid: SceneGrid;
  /** Omitted: everything drawn. */
  annotations?: SceneAnnotations;
  /** Scene units per metre, for converting Phase 2 placement. */
  placementScale: number;
  /**
   * A script step's connectivity preview (D42), drawn over the schematic:
   * what is open, and what each place is open to or filled with. Presentation
   * only; carried on the scene so an export draws what the screen does.
   */
  preview?: StepPreview | null;
  /**
   * What volumes and flows read in (D49): the design's display units. On the
   * scene, so an export reads as the screen does (invariant 7).
   */
  units?: LiquidUnits;
  /**
   * A plain channel longer than this (metre) gets the serpentine glyph. A
   * presentation choice derived from parameters, never from the drawn path.
   */
  serpentineThreshold: number;
}

/**
 * The serpentine threshold for a kind of design. A long cartridge channel is
 * folded into a serpentine when it is built; a long tube is just a long tube,
 * so a tubing set never gets the glyph (D34).
 */
export function serpentineThresholdFor(kind: DesignKind): number {
  return kind === "tubing_set" ? Number.POSITIVE_INFINITY : DEFAULT_VIEW.serpentineThreshold;
}

export const DEFAULT_VIEW: SceneView = {
  collapsed: new Set(),
  grid: { spacing: 16, visible: true },
  // 1 mm = 10 scene units: a 75 x 25 mm slide is 750 x 250, the same order as
  // the editor's symbols.
  placementScale: 1e4,
  // Anything much beyond the cartridge's short dimension has to fold.
  serpentineThreshold: 1e-2,
};
